import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Select,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from "@chakra-ui/react";
// import {  PieChart, BarChart } from '../../components/ChartCard';
import {
  LineChart,
  PieChart,
  BarChart,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Pie,
  Cell,
  Line,
} from "recharts";
import { List, ListItem, Divider } from "@chakra-ui/react";

// Sample total interaction data
const interactionData = [
  { day: "Mon", interactions: 20 },
  { day: "Tue", interactions: 45 },
  { day: "Wed", interactions: 32 },
  { day: "Thu", interactions: 50 },
  { day: "Fri", interactions: 35 },
  { day: "Sat", interactions: 25 },
  { day: "Sun", interactions: 40 },
];

const Analytics = () => {
  const [timeRange, setTimeRange] = useState("7d");
  const cardBg = useColorModeValue("white", "gray.700");
  const bg = useColorModeValue("white", "gray.700");
  const boxShadow = useColorModeValue("sm", "dark-lg");

  // Move analyticsData declaration before its usage
  const analyticsData = {
    totalConversations: 1500,
    activeUsers: 250,
    averageResponseTime: "1.2s",
    satisfactionRate: "95%",
    conversationsByDay: [
      { day: "Mon", count: 120 },
      { day: "Tue", count: 150 },
      { day: "Wed", count: 180 },
      { day: "Thu", count: 160 },
      { day: "Fri", count: 200 },
      { day: "Sat", count: 90 },
      { day: "Sun", count: 70 },
    ],
    conversationsByIntent: [
      { intent: "Support", count: 600 },
      { intent: "Sales", count: 400 },
      { intent: "Technical", count: 300 },
      { intent: "General", count: 200 },
    ],
    topIntents: [
      {
        intent: "Product Support",
        count: 250,
        successRate: "98%",
        avgResponseTime: "1.1s",
      },
      {
        intent: "Pricing Inquiry",
        count: 180,
        successRate: "96%",
        avgResponseTime: "1.3s",
      },
      {
        intent: "Technical Issue",
        count: 150,
        successRate: "92%",
        avgResponseTime: "1.5s",
      },
    ],
    userSatisfaction: [
      { rating: 5, count: 800 },
      { rating: 4, count: 400 },
      { rating: 3, count: 200 },
      { rating: 2, count: 50 },
      { rating: 1, count: 50 },
    ],
  };

  const interactionsByDay = analyticsData.conversationsByDay;
  const interactionsByPlatform = analyticsData.conversationsByIntent;
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  // Custom colors for different charts
  const chartColors = {
    primary: "#805AD5", // Purple
    secondary: "#38B2AC", // Teal
    accent: "#F6AD55", // Orange
    success: "#48BB78", // Green
    warning: "#ECC94B", // Yellow
    danger: "#F56565", // Red
  };

  return (
    <Box p={6}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg" color={chartColors.primary} mb={6}>
          Analytics Dashboard
        </Heading>

        {/* Line Chart for Interactions */}
        <Card bg={cardBg} p={6} borderRadius="xl" boxShadow={boxShadow}>
          <Heading size="md" mb={4} color={chartColors.primary}>
            Interaction Trends
          </Heading>
          <Box h="400px">
            <ResponsiveContainer>
              <LineChart data={interactionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="day"
                  stroke="#718096"
                  tick={{ fill: "#718096" }}
                />
                <YAxis stroke="#718096" tick={{ fill: "#718096" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: cardBg,
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "lg",
                  }}
                  labelStyle={{ color: "#718096" }}
                />
                <Line
                  type="monotone"
                  dataKey="interactions"
                  stroke={chartColors.primary}
                  strokeWidth={3}
                  dot={{
                    r: 6,
                    fill: chartColors.primary,
                    strokeWidth: 2,
                    stroke: "white",
                  }}
                  activeDot={{
                    r: 8,
                    fill: chartColors.primary,
                    strokeWidth: 2,
                    stroke: "white",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke={chartColors.secondary}
                  strokeWidth={3}
                  dot={{
                    r: 6,
                    fill: chartColors.secondary,
                    strokeWidth: 2,
                    stroke: "white",
                  }}
                  activeDot={{
                    r: 8,
                    fill: chartColors.secondary,
                    strokeWidth: 2,
                    stroke: "white",
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card>

        {/* Interactions by Day */}
        <Box>
          <Heading as="h2" size="lg" mb={4}>
            Interactions by Day
          </Heading>
          <Box height="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={interactionsByDay}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3182ce" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Interactions by Platform */}
        <Box>
          <Heading as="h2" size="lg" mb={4}>
            Interactions by Platform
          </Heading>
          <HStack spacing={8} align="start">
            <Box width="50%" height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={interactionsByPlatform}
                    dataKey="count"
                    nameKey="platform"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    fill="#8884d8"
                    label
                  >
                    {interactionsByPlatform.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.platform || index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box width="50%">
              <Heading as="h3" size="md" mb={2}>
                Platform List
              </Heading>
              <List spacing={2}>
                {interactionsByPlatform.map((item, index) => (
                  <ListItem key={`platform-${item.platform || index}`}>
                    <HStack justify="space-between">
                      <Text>{item.platform}</Text>
                      <Text fontWeight="bold">{item.count}</Text>
                    </HStack>
                    {index !== interactionsByPlatform.length - 1 && <Divider />}
                  </ListItem>
                ))}
              </List>
            </Box>
          </HStack>
        </Box>

        {/* Time Range Selector */}
        {/* <Select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          maxW="200px"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </Select> */}

        {/* Stats Cards */}
        {/* <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel>Total Conversations</StatLabel>
                <StatNumber>{analyticsData.totalConversations}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  23.36%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel>Active Users</StatLabel>
                <StatNumber>{analyticsData.activeUsers}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  12.5%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel>Average Response Time</StatLabel>
                <StatNumber>{analyticsData.averageResponseTime}</StatNumber>
                <StatHelpText>
                  <StatArrow type="decrease" />
                  5.2%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <StatLabel>Satisfaction Rate</StatLabel>
                <StatNumber>{analyticsData.satisfactionRate}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  2.5%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid> */}

        {/* Charts */}
        {/* <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg={cardBg}>
            <CardHeader>
              <Heading size="sm">Conversation Trends</Heading>
            </CardHeader>
            <CardBody>
              <LineChart
                data={analyticsData.conversationsByDay}
                xKey="day"
                yKey="count"
                title="Daily Conversations"
              />
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardHeader>
              <Heading size="sm">Conversations by Intent</Heading>
            </CardHeader>
            <CardBody>
              <PieChart
                data={analyticsData.conversationsByIntent}
                nameKey="intent"
                valueKey="count"
                title="Intent Distribution"
              />
            </CardBody>
          </Card>
        </SimpleGrid> */}

        {/* Top Intents Table */}
        {/* <Card bg={cardBg}>
          <CardHeader>
            <Heading size="sm">Top Intents</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Intent</Th>
                  <Th>Count</Th>
                  <Th>Success Rate</Th>
                  <Th>Avg Response Time</Th>
                </Tr>
              </Thead>
              <Tbody>
                {analyticsData.topIntents.map((intent, index) => (
                  <Tr key={index}>
                    <Td>{intent.intent}</Td>
                    <Td>{intent.count}</Td>
                    <Td>
                      <Badge colorScheme="green">{intent.successRate}</Badge>
                    </Td>
                    <Td>{intent.avgResponseTime}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card> */}

        {/* User Satisfaction */}
        {/* <Card bg={cardBg}>
          <CardHeader>
            <Heading size="sm">User Satisfaction</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {analyticsData.userSatisfaction.map((rating) => (
                <Box key={rating.rating}>
                  <HStack justify="space-between" mb={2}>
                    <Text>{rating.rating} Stars</Text>
                    <Text>{rating.count} users</Text>
                  </HStack>
                  <Progress
                    value={(rating.count / analyticsData.totalConversations) * 100}
                    colorScheme="purple"
                    size="sm"
                  />
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card> */}
      </VStack>
    </Box>
  );
};

export default Analytics;
